import styled from '@emotion/styled';
import type { RefObject, MutableRefObject } from 'react';
import { v4 as generateUUID } from 'uuid';
import {
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
  createRef,
  Children,
  cloneElement,
} from 'react';
import type { HeadRowProps } from './HeadRow';
import { StyledHeadRow } from './TableParts';
import type { HeadCellType } from './HeadCell';

const FixedRow = styled(StyledHeadRow)<HeadRowProps>`
  top: 0;
  position: fixed;
  display: flex;
  overflow: hidden;
  z-index: 1;

  > * {
    flex-shrink: 0;
  }

  ${({ isVisible }) =>
    !isVisible &&
    `
    visibility: hidden;
  `};
`;

FixedRow.defaultProps = {
  isVisible: false,
};

export interface StickyHeadRowProps extends HeadRowProps {
  /**
   * Offset (from the top) to position the sticky row.
   * Defaults to 0.
   */
  offset?: number;
}

/**
 * Updates the refs to all the header columns.
 * This will be used to sync the positions and focus states between the original
 * row and the fixed positioned row.
 * We can assume both of the row and fixedRow will have the same content.
 */
const updateHeaderRefs = (
  headerRefs: RefObject<HTMLElement>[],
  fixedHeaderRefs: RefObject<HTMLElement>[],
  rowRef: RefObject<HTMLElement>,
  fixedRowRef: RefObject<HTMLElement>,
) => {
  headerRefs.length = 0;
  fixedHeaderRefs.length = 0;

  if (!rowRef.current) {
    return;
  }

  for (let i = 0; i < rowRef.current.children.length; i += 1) {
    const child = rowRef.current.children[i] as HTMLElement;
    const fixedChild = fixedRowRef.current?.children[i] as HTMLElement;

    if (!child.getAttribute('data-id')) {
      const headerId = `header-${generateUUID()}`;
      // only add it to the ones that don't have one already
      child.setAttribute('data-id', headerId);
      fixedChild.setAttribute('data-id', headerId);
    }
    const ref = createRef<HTMLElement>() as MutableRefObject<HTMLElement>;
    const fixedRef = createRef<HTMLElement>() as MutableRefObject<HTMLElement>;
    ref.current = child;
    fixedRef.current = fixedChild;
    headerRefs.push(ref);
    fixedHeaderRefs.push(fixedRef);
  }
};

/**
 * Syncs the focus state on the sort buttons and the checkbox (selectable column)
 * between the fixed row and the original one.
 */
const syncFocusState = (headerRefs: MutableRefObject<HTMLElement | null>[]) => {
  const activeElement = document.activeElement as HTMLElement;
  const elementTag = activeElement.tagName.toLowerCase();
  const parentColumn = activeElement.closest('th');

  if (
    (elementTag === 'button' || elementTag === 'input') &&
    parentColumn &&
    parentColumn.getAttribute('data-id')?.includes('header-')
  ) {
    const matchedHeaderRef = headerRefs.find(
      (element) =>
        element.current &&
        element.current.getAttribute('data-id') ===
          parentColumn.getAttribute('data-id'),
    );

    (
      matchedHeaderRef?.current?.querySelector(elementTag) as HTMLElement
    ).focus();
  }
};

// aligns the headers from the FixedRow with the correspondent horizontal scroll position.
// only the scrollable header columns are taken into account (if we have a fixed right column, that one will be ignored).
const alignHeadersWithTableScrollOffset = (
  fixedHeaderRefs: RefObject<HTMLElement>[],
  scrollParentRef: MutableRefObject<HTMLElement | null>,
) => {
  fixedHeaderRefs
    .map((ref) => ref.current)
    .forEach((element) => {
      if (element && !element.classList.contains('isSticky')) {
        element.style.transform = `translateX(-${
          scrollParentRef.current ? scrollParentRef.current.scrollLeft : 0
        }px)`;
      }
    });
};

const updateHeaderParentScroll = (
  fixedHeaderRefs: RefObject<HTMLElement>[],
  scrollParentRef: MutableRefObject<HTMLElement | null>,
  fixedRowRef: RefObject<HTMLElement>,
) => {
  // Move the fixed row to match the scroll parent position
  const currentScrollBoundingRect =
    scrollParentRef.current?.getBoundingClientRect();

  if (currentScrollBoundingRect && fixedRowRef.current) {
    fixedRowRef.current.style.left = `${currentScrollBoundingRect.left}px`;
  }

  alignHeadersWithTableScrollOffset(fixedHeaderRefs, scrollParentRef);
};

const updateHeaderSize = (
  newHeaderMeasures: DOMRect[],
  fixedHeaderRefs: RefObject<HTMLElement>[],
  scrollParentRef: MutableRefObject<HTMLElement | null>,
  fixedRowRef: RefObject<HTMLElement>,
) => {
  // Update the fixed headers sizes to match the original headers
  fixedHeaderRefs.forEach((fixedRef, index) => {
    const rect = newHeaderMeasures[index];

    if (rect === undefined || !fixedRef.current) return;

    fixedRef.current.style.width = `${rect.width}px`;
    fixedRef.current.style.height = `${rect.height}px`;
  });

  // Move the fixed row to match the scroll parent position
  const currentScrollBoundingRect =
    scrollParentRef.current?.getBoundingClientRect();

  if (currentScrollBoundingRect && fixedRowRef.current) {
    fixedRowRef.current.style.width = `${currentScrollBoundingRect.width}px`;
  }
};

const getHeaderMeasures = (
  rowRef: RefObject<HTMLElement>,
  headerRefs: RefObject<HTMLElement>[],
  fixedRowRef: RefObject<HTMLElement>,
): DOMRect[] | undefined => {
  if (!rowRef.current || !fixedRowRef.current) return;

  // gets the new measurements/positions of the headers (original row)
  return headerRefs.reduce<DOMRect[]>((result, ref: RefObject<HTMLElement>) => {
    if (!ref.current) return result;

    const rect = ref.current.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) return result;

    result.push(rect);

    return result;
  }, []);
};

/**
 * StickyHeadRow component
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 *
 * @summary It's used to create a duplicated header row with a fixed positioning,
 * which will be used to programmatically get a sticky effect on the
 * table header.
 * When the scroll is on the top of the window, the original row (StyledHeadRow) is shown, but when the user
 * scrolls down and the header becomes fixed, the row used is the FixedRow.
 *
 * @private
 */
export const StickyHeadRow = ({
  children,
  offset = 0,
}: StickyHeadRowProps): JSX.Element => {
  const [isFixed, setFixed] = useState<boolean>(false);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const fixedRowRef = useRef<HTMLTableRowElement>(null);

  const headerRefs = useRef<RefObject<HTMLElement>[]>([]);
  const fixedHeaderRefs = useRef<RefObject<HTMLElement>[]>([]);

  const scrollParentRef = useRef<HTMLElement | null>(null);

  // Create reference to the parent container
  useLayoutEffect(() => {
    scrollParentRef.current = rowRef.current
      ? rowRef.current.closest('div[data-id="dsTableContainer"]')
      : null;
  }, []);

  // Create references to the header elements
  // needs to re-run everytime the children change,
  // because it's possible to add/remove columns to the table dynamically
  useLayoutEffect(() => {
    updateHeaderRefs(
      headerRefs.current,
      fixedHeaderRefs.current,
      rowRef,
      fixedRowRef,
    );
  }, [children]);

  const updateHeaderScroll = useCallback(
    (newHeaderMeasures: DOMRect[]) => {
      // checks if the headers (original row) of the table have passed the offset defined.
      // if they have, we'll need to switch to the fixed row.
      // we're using the first column available for the calculation.
      const firstMeasure = newHeaderMeasures.find((r) => r !== undefined);
      const isScrolledBeyondOffset =
        firstMeasure !== undefined && firstMeasure.top < offset;

      if (!isScrolledBeyondOffset && isFixed) {
        setFixed(false);

        syncFocusState(headerRefs.current);

        return; // no need for more extra work. we're now seeing the original header
      }

      // Move the fixed row to the offset position
      if (fixedRowRef.current) fixedRowRef.current.style.top = `${offset}px`;

      if (isScrolledBeyondOffset && !isFixed) {
        setFixed(true);
      }

      alignHeadersWithTableScrollOffset(
        fixedHeaderRefs.current,
        scrollParentRef,
      );

      syncFocusState(fixedHeaderRefs.current);
    },
    [isFixed],
  );

  // Track document scroll position
  useLayoutEffect(() => {
    const onScroll = () => {
      if (isFixed) {
        updateHeaderParentScroll(
          fixedHeaderRefs.current,
          scrollParentRef,
          fixedRowRef,
        );
      }
      const headerMeasures = getHeaderMeasures(
        rowRef,
        headerRefs.current,
        fixedRowRef,
      );
      if (headerMeasures) updateHeaderScroll(headerMeasures);
    };
    document.addEventListener('scroll', onScroll, { passive: true });
    return () => document.removeEventListener('scroll', onScroll);
  }, [children, isFixed]);

  // Track scrollParent size
  useLayoutEffect(() => {
    if (!scrollParentRef.current) return () => null;

    const onResize = () => {
      if (isFixed) {
        updateHeaderParentScroll(
          fixedHeaderRefs.current,
          scrollParentRef,
          fixedRowRef,
        );
      }
      const headerMeasures = getHeaderMeasures(
        rowRef,
        headerRefs.current,
        fixedRowRef,
      );
      if (headerMeasures)
        updateHeaderSize(
          headerMeasures,
          fixedHeaderRefs.current,
          scrollParentRef,
          fixedRowRef,
        );
    };

    const observer = new ResizeObserver(onResize);
    observer.observe(scrollParentRef.current);

    return () => observer.disconnect();
  }, [children, isFixed]);

  // Track scrollParent scroll position
  useLayoutEffect(() => {
    const scrollParent = scrollParentRef.current;

    if (!scrollParent) return () => null;

    const onScroll = () => {
      if (isFixed) {
        updateHeaderParentScroll(
          fixedHeaderRefs.current,
          scrollParentRef,
          fixedRowRef,
        );
      }
    };

    scrollParent.addEventListener('scroll', onScroll, {
      passive: true,
    });
    return () => scrollParent.removeEventListener('scroll', onScroll);
  }, [children, isFixed]);

  // FIXME: The typing for this map would fail in strict mode.
  // We need to account for a child being an array.
  const mappedChildren = Children.map(children, (child: HeadCellType) => {
    return child
      ? cloneElement(child, {
          ...child.props,
          isFixed,
          isVisible: !isFixed,
        })
      : null;
  });

  // FIXME: The typing for this map would fail in strict mode.
  // We need to account for a child being an array.
  const mappedFixedChildren = Children.map(children, (child: HeadCellType) => {
    return child
      ? cloneElement(child, {
          ...child.props,
          isFixed,
          isVisible: isFixed,
        })
      : null;
  });

  // ensure header updates are applied on render
  useLayoutEffect(() => {
    const measures = getHeaderMeasures(rowRef, headerRefs.current, fixedRowRef);

    if (measures) {
      updateHeaderSize(
        measures,
        fixedHeaderRefs.current,
        scrollParentRef,
        fixedRowRef,
      );
      updateHeaderScroll(measures);
    }
    if (isFixed) {
      updateHeaderParentScroll(
        fixedHeaderRefs.current,
        scrollParentRef,
        fixedRowRef,
      );
    }
  }, [children, isFixed]);

  return (
    <>
      <StyledHeadRow ref={rowRef} isVisible={!isFixed}>
        {mappedChildren as HeadCellType[]}
      </StyledHeadRow>
      <FixedRow ref={fixedRowRef} isVisible={isFixed}>
        {mappedFixedChildren as HeadCellType[]}
      </FixedRow>
    </>
  );
};
